import type { ConditionGroup, NextPageParams, PaginationMetadata } from "@/schema"
import type { CreatePastClientPayload, PastClient } from "@/schema/past-client"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"
import { BulkAddHouseholdsToProjectResponse } from "@/hooks/api/projects/types"

export interface PastClientsResponse {
  past_clients: PastClient[]
  pagination: PaginationMetadata
}

export function fetchPastClients(
  context: ApiContext,
  viewFilterGroup?: ConditionGroup,
  nextPageParams?: NextPageParams,
  searchTerm?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const params = new URLSearchParams()

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  appendViewSortParams(params, viewSort)

  return apiContextFetch<PastClientsResponse>(context, `past_clients?${params.toString()}`, {
    method: "GET",
  })
}

export function fetchPastClient(context: ApiContext, id: string) {
  return apiContextFetch<PastClient>(context, `past_clients/${id}`)
}

export function createPastClient(context: ApiContext, params: CreatePastClientPayload, overrideBookId?: string) {
  return apiContextFetch<PastClient>(context, "past_clients", {
    method: "POST",
    body: JSON.stringify({ past_client: params, override_book_id: overrideBookId }),
  })
}

export interface UpdatePastClientPayload {
  custom_fields_attributes?: Record<string, unknown>
  [key: string]: unknown
}

export function updatePastClient(context: ApiContext, id: string, params: UpdatePastClientPayload) {
  return apiContextFetch<PastClient>(context, `past_clients/${id}`, {
    method: "PUT",
    body: JSON.stringify({ past_client: params }),
  })
}

export interface BulkUpdatePastClientsPayload {
  past_client_ids: string[]
  tag_id?: string
  remove_tag_id?: string
}

export function bulkUpdatePastClients(context: ApiContext, params: BulkUpdatePastClientsPayload) {
  return apiContextFetch<{ updated_count: number }>(context, "past_clients/bulk_update", {
    method: "PATCH",
    body: JSON.stringify(params),
  })
}

export interface BulkAddPastClientsToProjectPayload {
  past_client_ids: string[]
  project_template_id: string
  assigned_to_id: string | null
  assigned_team_id: string | null
}

export function bulkAddPastClientsToProject(context: ApiContext, params: BulkAddPastClientsToProjectPayload) {
  return apiContextFetch<BulkAddHouseholdsToProjectResponse>(context, "past_clients/bulk_add_to_project", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface AllPastClientIdsResponse {
  past_client_ids: string[]
  total_count: number
}

export function fetchAllPastClientIds(context: ApiContext, viewFilterGroup?: ConditionGroup, searchTerm?: string) {
  const params = new URLSearchParams()

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  return apiContextFetch<AllPastClientIdsResponse>(context, `past_clients/all_ids?${params.toString()}`, {
    method: "GET",
  })
}

export interface BulkCreatePastClientsPayload {
  file_name?: string
  override_duplicates?: boolean
  records: Array<{
    household_id?: string
    household_name?: string
    contact_type?: string
    full_name?: string
    first_name?: string
    last_name?: string
    email?: string
    email_type?: string
    email_2?: string
    email_2_type?: string
    email_3?: string
    email_3_type?: string
    mobile_phone?: string
    home_phone?: string
    work_phone?: string
    fax_phone?: string
    other_phone?: string
    ssn?: string
    date_of_birth?: string
    gender?: string
    marital_status?: string
    job_title?: string
    salutation?: string
    suffix?: string
    middle_name?: string
    preferred_name?: string
    designations?: string
    tier?: string
    address_line1?: string
    address_line2?: string
    address_city?: string
    address_state?: string
    address_zip?: string
    address_country_code?: string
    address_type?: string
    is_primary_address?: string
    address_2_line1?: string
    address_2_line2?: string
    address_2_city?: string
    address_2_state?: string
    address_2_zip?: string
    address_2_country_code?: string
    address_2_type?: string
    tags?: string
    custom_fields?: Record<string, unknown>
  }>
}

export interface BulkCreatePastClientsResponse {
  list_id: string
}

export function bulkCreatePastClients(context: ApiContext, params: BulkCreatePastClientsPayload) {
  return apiContextFetch<BulkCreatePastClientsResponse>(context, "past_clients/bulk_create", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export function bulkDestroyPastClients(context: ApiContext, pastClientIds: string[]) {
  return apiContextFetch<PastClient>(context, `past_clients/bulk_destroy`, {
    method: "DELETE",
    body: JSON.stringify({ past_client_ids: pastClientIds }),
  })
}

export async function restoreToClient(context: ApiContext, pastClientId: string) {
  return await apiContextFetch<PastClient>(context, `past_clients/${pastClientId}/restore_to_client`, {
    method: "POST",
  })
}

export interface BulkRestoreToClientPayload {
  past_client_ids: string[]
}

export function bulkRestoreToClient(context: ApiContext, params: BulkRestoreToClientPayload) {
  return apiContextFetch<{ restored_count: number; failed_count: number }>(
    context,
    "past_clients/bulk_restore_to_client",
    {
      method: "POST",
      body: JSON.stringify(params),
    }
  )
}
